/**
 * src/services/spatial/geoLinkSpatialObjectsClient.ts
 *
 * Client for the GeoLink spatial-objects API. GeoTrust treats GeoLink
 * as the source of truth for buildings/floors/zones/slices and for
 * execution authorisation — this client is the only place that talks
 * to those endpoints.
 *
 * Surface (per the GeoLink ↔ GeoTrust integration guide):
 *
 *   GET   /api/spatial-objects                       → list (filters: bbox, near, radius, owner_wallet, parent_object_id, …)
 *   GET   /api/spatial-objects/:id                   → single canonical object
 *   GET   /api/spatial-objects/render?provider=…     → envelope { provider, featureCollection, style }
 *   GET   /api/spatial-objects/:id/render?provider=… → single-feature envelope
 *   GET   /api/spatial-objects/:id/interactions      → declared verbs + recent decisions
 *   POST  /api/spatial-objects/:id/interact          → record interaction → { interaction: { decision, decisionReason, id } }
 *   POST  /api/spatial-objects/:id/execute           → authorize execution → { status: "authorized", execution: { ... } }
 *   PATCH /api/spatial-objects/executions/:execId    → close audit trail with tx_hash
 *
 * Critical contract (quoted from the integration guide):
 *
 *   "GeoTrust MUST NOT broadcast a Soroban tx without a 200 response
 *    from /execute."
 *
 * The client always hits real GeoLink endpoints — there is no mock
 * fallback. When GeoLink has no spatial objects in the current bbox
 * the map simply renders no objects, which is the correct behaviour.
 */

import {
  getGeoLinkApiBaseUrl,
  getSpatialRenderProvider,
} from './spatialEnv';
import type { ProviderBounds } from './mapProvider';
import type {
  SpatialObjectExecuteRequest,
  SpatialObjectExecuteResponse,
  SpatialObjectExecutionResultBody,
  SpatialObjectFeature,
  SpatialObjectFeatureCollection,
  SpatialObjectInteractRequest,
  SpatialObjectInteractResponse,
  SpatialObjectInteractionAction,
  SpatialObjectInteractions,
  SpatialObjectsRenderEnvelope,
  SpatialObjectType,
  SpatialRenderProvider,
  SpatialRenderStyleHint,
} from './geoLinkTypes';

/**
 * The verbs that may invoke a contract.
 *
 * The one place this list exists. It used to be written out twice — here
 * and again at the panel's dispatch boundary — and both copies ended in
 * `|| !!contractFunction`, which made the verb irrelevant: any object
 * with a linked function handed that function to every verb it declared,
 * `view` included. A verb is the authority on whether invoking a contract
 * is the point of it, so nothing else gets to decide.
 */
export const CONTRACT_VERBS: ReadonlySet<string> = new Set(['execute', 'pay', 'buy']);

/**
 * Whether this verb may invoke a contract.
 *
 * Takes the verb alone on purpose. A `contractFunction` sitting on an
 * action is data the object supplied; it is not a grant.
 */
export function isContractVerb(verb: string | null | undefined): boolean {
  return !!verb && CONTRACT_VERBS.has(verb);
}

/** Public verbs we know how to surface as actions in the panel UI. */
const VERB_LABELS: Record<string, { label: string; description: string }> = {
  tap:     { label: 'View metadata',      description: 'Records a public tap interaction with this object.' },
  view:    { label: 'View metadata',      description: 'Opens the metadata panel.' },
  execute: { label: 'Execute linked rule',description: 'Asks GeoLink to authorise the linked rule, then signs + broadcasts.' },
  enter:   { label: 'Enter',              description: 'Records a check-in event on-chain.' },
  pay:     { label: 'Pay',                description: 'Charges the configured asset and broadcasts a Soroban tx.' },
  buy:     { label: 'Buy',                description: 'Purchases the linked listing.' },
  open:    { label: 'Open',               description: 'Opens any linked metadata or external resource.' },
};

function bboxQuery(b?: ProviderBounds | null): string {
  if (!b) return '';
  const { sw, ne } = b;
  // GeoLink's bbox order is `minLng,minLat,maxLng,maxLat` per the guide.
  return `bbox=${sw.lng.toFixed(6)},${sw.lat.toFixed(6)},${ne.lng.toFixed(6)},${ne.lat.toFixed(6)}`;
}

function coerceProvider(p?: SpatialRenderProvider): SpatialRenderProvider {
  return p ?? getSpatialRenderProvider();
}

/** Synthesise a UI-ready action list from the object's `interactionTypes`. */
function actionsForVerbs(verbs: string[], contractFunction?: string | null): SpatialObjectInteractionAction[] {
  if (!Array.isArray(verbs) || verbs.length === 0) {
    return [{
      id:    'view',
      name:  'view',
      label: 'View metadata',
      description: 'No interaction verbs declared for this object — opens metadata only.',
    }];
  }
  return verbs.map(verb => {
    const known = VERB_LABELS[verb] ?? { label: verb, description: `Record a "${verb}" interaction with GeoLink.` };
    return {
      id:    verb,
      name:  verb,
      label: known.label,
      description: known.description,
      // The object's linked function reaches only the verbs that exist to
      // call it. An unknown verb gets nothing, because nothing says what
      // it was meant to do.
      contractFunction: isContractVerb(verb) && contractFunction ? contractFunction : undefined,
    };
  });
}

class GeoLinkSpatialObjectsClient {
  private baseUrl: string;
  private apiKey: string | null;
  private walletProviderKey: string | null;

  constructor(opts: {
    baseUrl?: string;
    apiKey?: string | null;
    walletProviderKey?: string | null;
  } = {}) {
    this.baseUrl = (opts.baseUrl ?? getGeoLinkApiBaseUrl()).replace(/\/+$/, '');
    this.apiKey = opts.apiKey ?? null;
    this.walletProviderKey = opts.walletProviderKey ?? null;
  }

  /**
   * Internal request helper.
   *
   * Browser callers send NO auth header: `getGeoLinkApiBaseUrl()` returns the
   * GeoTrust backend proxy, which injects the server-side key and discards
   * anything the browser sent. Keys only appear when a Node/test caller passes
   * them explicitly to the constructor and talks to GeoLink directly.
   */
  private async request<T>(
    path: string,
    init: RequestInit & { useWalletProviderKey?: boolean } = {},
  ): Promise<T> {
    const useWallet = init.useWalletProviderKey === true;
    const apiKey = useWallet ? this.walletProviderKey : this.apiKey;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(init.headers as Record<string, string> | undefined),
    };

    if (apiKey && apiKey.trim()) {
      headers['X-API-Key'] = apiKey.trim();
    }

    const url = `${this.baseUrl}${path.startsWith('/') ? path : '/' + path}`;
    const fetchOpts: RequestInit = { ...init, headers };
    delete (fetchOpts as { useWalletProviderKey?: boolean }).useWalletProviderKey;

    const res = await fetch(url, fetchOpts);
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try {
        const body = await res.json();
        msg = body?.reason || body?.error || body?.message || msg;
      } catch { /* ignore */ }
      const err = new Error(`[geoLinkSpatial] ${path}: ${msg}`);
      (err as { status?: number }).status = res.status;
      throw err;
    }

    const text = await res.text();
    if (!text) return undefined as unknown as T;
    try {
      return JSON.parse(text) as T;
    } catch {
      throw new Error(`[geoLinkSpatial] ${path}: invalid JSON response`);
    }
  }

  // ─── Reads ────────────────────────────────────────────────────────

  /**
   * `GET /api/spatial-objects?bbox=…` — list canonical objects in a
   * bounding box. Returns just metadata (no render envelope); use
   * `getVisibleSpatialObjectsRender` for the render-ready GeoJSON.
   */
  async listSpatialObjects(bounds: ProviderBounds | null): Promise<SpatialObjectFeature[]> {
    const q = bboxQuery(bounds);
    const path = `/api/spatial-objects${q ? `?${q}` : ''}`;
    const json = await this.request<{
      features?: SpatialObjectFeature[];
      spatialObjects?: SpatialObjectFeature[];
    } | SpatialObjectFeatureCollection>(path);

    if (Array.isArray((json as SpatialObjectFeatureCollection).features)
        && (json as SpatialObjectFeatureCollection).type === 'FeatureCollection') {
      return (json as SpatialObjectFeatureCollection).features;
    }
    const list = (json as { features?: SpatialObjectFeature[]; spatialObjects?: SpatialObjectFeature[] });
    return list.features ?? list.spatialObjects ?? [];
  }

  /** `GET /api/spatial-objects/:id` — single canonical object (no render hints). */
  async getSpatialObject(id: string): Promise<SpatialObjectFeature | null> {
    try {
      const json = await this.request<SpatialObjectFeature | { feature?: SpatialObjectFeature }>(
        `/api/spatial-objects/${encodeURIComponent(id)}`,
      );
      return (json as { feature?: SpatialObjectFeature }).feature ?? (json as SpatialObjectFeature);
    } catch (e) {
      if ((e as { status?: number }).status === 404) return null;
      throw e;
    }
  }

  /**
   * `GET /api/spatial-objects/:id/render` — single-feature envelope.
   * Use this when you want the GeoLink-supplied style hint for one
   * object (rare — the bulk endpoint is what powers the map layer).
   */
  async getSpatialObjectRender(
    id: string,
    provider?: SpatialRenderProvider,
  ): Promise<SpatialObjectsRenderEnvelope | null> {
    const p = coerceProvider(provider);
    try {
      return await this.request<SpatialObjectsRenderEnvelope>(
        `/api/spatial-objects/${encodeURIComponent(id)}/render?provider=${p}`,
      );
    } catch (e) {
      if ((e as { status?: number }).status === 404) return null;
      throw e;
    }
  }

  /**
   * `GET /api/spatial-objects/render?provider=…&bbox=…` — bulk render
   * endpoint. This is what `SpatialObjectsMapLayer` calls every time
   * the camera moves. Returns the canonical envelope:
   *
   *     { provider, featureCollection, style: { sourceId, sourceType, layers } }
   *
   * The `style.layers` block is a SUGGESTION GeoTrust may override —
   * we use it as a starting point but our layer components apply
   * GeoTrust-flavoured paint expressions on top so the look is
   * consistent across providers.
   */
  async getVisibleSpatialObjectsRender(
    bounds: ProviderBounds | null,
    provider?: SpatialRenderProvider,
  ): Promise<SpatialObjectsRenderEnvelope> {
    const p = coerceProvider(provider);
    const q = bboxQuery(bounds);
    const path = `/api/spatial-objects/render?provider=${p}${q ? `&${q}` : ''}`;
    const json = await this.request<SpatialObjectsRenderEnvelope | { featureCollection?: SpatialObjectFeatureCollection; style?: SpatialRenderStyleHint }>(
      path,
    );

    // Default suggested style block — used as a backup when GeoLink
    // returns a FeatureCollection without the new envelope wrapper.
    // GeoTrust's layer components apply their own paint expressions
    // on top so this only matters as a structural placeholder.
    const fallbackStyle: SpatialRenderStyleHint = {
      sourceId:   'geolink-spatial-objects',
      sourceType: 'geojson',
      layers: [],
    };

    // Tolerate slightly different envelopes (older GeoLink builds
    // returned the FeatureCollection directly without the wrapper).
    if ((json as SpatialObjectsRenderEnvelope).featureCollection) {
      return json as SpatialObjectsRenderEnvelope;
    }
    if ((json as SpatialObjectFeatureCollection).type === 'FeatureCollection') {
      return {
        provider: p,
        featureCollection: json as SpatialObjectFeatureCollection,
        style: fallbackStyle,
      };
    }
    return {
      provider: p,
      featureCollection: { type: 'FeatureCollection', features: [] },
      style: fallbackStyle,
    };
  }

  /**
   * `GET /api/spatial-objects/:id/interactions` — declared interaction
   * verbs + recent decisions. We denormalise the linked rule from the
   * spatial object itself so the panel can render the metadata block
   * without a second round trip.
   */
  async getSpatialObjectInteractions(id: string): Promise<SpatialObjectInteractions> {
    const raw = await this.request<{
      objectId?: string;
      name?: string | null;
      description?: string | null;
      objectType?: SpatialObjectType;
      interactionTypes?: string[];
      recent?: SpatialObjectInteractions['recentInteractions'];
      // Denormalised fields some GeoLink builds include:
      executionRuleId?: number | string | null;
      contractAddress?: string | null;
      contractFunction?: string | null;
      metadata?: Record<string, unknown>;
    }>(
      `/api/spatial-objects/${encodeURIComponent(id)}/interactions`,
    );

    const verbs = raw.interactionTypes ?? [];
    return {
      spatialObjectId:  raw.objectId ?? id,
      name:             raw.name ?? null,
      description:      raw.description ?? null,
      objectType:       raw.objectType,
      interactionTypes: verbs,
      actions:          actionsForVerbs(verbs, raw.contractFunction),
      executionRule:    raw.executionRuleId
        ? {
            ruleId:           raw.executionRuleId,
            contractAddress:  raw.contractAddress ?? null,
            contractFunction: raw.contractFunction ?? null,
          }
        : null,
      recentInteractions: raw.recent ?? [],
      metadata:          raw.metadata,
    };
  }

  // ─── Writes (gated execution flow) ────────────────────────────────

  /**
   * `POST /api/spatial-objects/:id/interact` — record a public
   * interaction. GeoLink replies with `{ interaction: { decision,
   * decisionReason, id } }`. Returns the decision verbatim — the
   * panel uses `decision === 'allowed'` to gate the next step.
   */
  async interactWithSpatialObject(
    id: string,
    payload: SpatialObjectInteractRequest,
  ): Promise<SpatialObjectInteractResponse> {
    if (!payload.interaction_type) {
      throw new Error('[geoLinkSpatial] interact: interaction_type is required');
    }
    return this.request<SpatialObjectInteractResponse>(
      `/api/spatial-objects/${encodeURIComponent(id)}/interact`,
      {
        method: 'POST',
        body:   JSON.stringify(payload),
      },
    );
  }

  /**
   * `POST /api/spatial-objects/:id/execute` — ask GeoLink to authorise
   * execution against the linked rule/contract.
   *
   *   "GeoTrust MUST NOT broadcast a Soroban tx without a 200 response
   *    from /execute." (integration guide §1.2)
   *
   * On success returns `{ status: "authorized", execution: { ... } }`
   * and the caller signs + broadcasts the inner Soroban tx using the
   * `execution.contractAddress` / `execution.functionName` /
   * `execution.functionParameters` fields.
   */
  async executeSpatialObjectRule(
    id: string,
    payload: SpatialObjectExecuteRequest,
  ): Promise<SpatialObjectExecuteResponse> {
    if (!payload.actor_wallet) {
      throw new Error('[geoLinkSpatial] execute: actor_wallet is required');
    }
    return this.request<SpatialObjectExecuteResponse>(
      `/api/spatial-objects/${encodeURIComponent(id)}/execute`,
      {
        method: 'POST',
        body:   JSON.stringify(payload),
      },
    );
  }

  /**
   * `PATCH /api/spatial-objects/executions/:execId` — close the audit
   * trail with the broadcast result. Call this after the wallet
   * signs and submits the Soroban tx.
   */
  async reportExecutionResult(
    executionId: string | number,
    body: SpatialObjectExecutionResultBody,
  ): Promise<{ ok?: boolean; status?: string; updated?: boolean } | null> {
    if (!executionId) {
      throw new Error('[geoLinkSpatial] reportExecutionResult: executionId is required');
    }
    try {
      return await this.request<{ ok?: boolean; status?: string; updated?: boolean }>(
        `/api/spatial-objects/executions/${encodeURIComponent(String(executionId))}`,
        {
          method: 'PATCH',
          body:   JSON.stringify(body),
        },
      );
    } catch (e) {
      // Audit-trail close-out is best-effort — never let it fail the
      // user's flow. Surface the error to the console for monitoring.
      return null;
    }
  }
}

/** Singleton client used across the spatial-objects subsystem. */
export const geoLinkSpatialObjectsClient = new GeoLinkSpatialObjectsClient();

export type { GeoLinkSpatialObjectsClient };
